import React from 'react';
import { ArrowRight, Download } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { heroContent } from '../data/mock';
import { toast } from '../hooks/use-toast';

const HeroSection = () => {
  const { language } = useLanguage();

  const handleStartJourney = () => {
    // Scroll to features section
    const featuresSection = document.getElementById('features');
    if (featuresSection) {
      featuresSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleDownloadApp = () => {
    toast({
      title: language === 'en' ? 'Coming Soon!' : '¡Próximamente!',
      description: language === 'en' 
        ? 'The Wallness app will be available soon on iOS and Android.' 
        : 'La app The Wallness estará disponible pronto en iOS y Android.',
    });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#0d1f1f] via-[#1a3334] to-[#0d1f1f]">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Brick Pattern Overlay */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }}></div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 py-32 text-center">
        {/* Main Content */}
        <div className="space-y-8">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full px-6 py-2 backdrop-blur-sm">
            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
            <span className="text-emerald-300 text-sm font-medium">
              {language === 'en' ? 'Founded by Christiane Endler' : 'Fundado por Christiane Endler'}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold text-white leading-tight">
            {language === 'en' ? heroContent.title_en : heroContent.title_es}
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
              {language === 'en' ? 'Brick by Brick' : 'Ladrillo por Ladrillo'}
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            {language === 'en' ? heroContent.description_en : heroContent.description_es}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8">
            <Button
              size="lg"
              onClick={handleStartJourney}
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg font-semibold rounded-full shadow-lg shadow-emerald-500/50 hover:shadow-emerald-500/70 transition-all duration-300 group"
            >
              {language === 'en' ? heroContent.cta_primary_en : heroContent.cta_primary_es}
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={handleDownloadApp}
              className="border-2 border-emerald-400 text-emerald-400 hover:bg-emerald-400 hover:text-[#0d1f1f] px-8 py-6 text-lg font-semibold rounded-full transition-all duration-300"
            >
              <Download className="mr-2" />
              {language === 'en' ? heroContent.cta_secondary_en : heroContent.cta_secondary_es}
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-16 max-w-4xl mx-auto">
            {[
              { number: '100K+', label_en: 'Active Users', label_es: 'Usuarios Activos' },
              { number: '50+', label_en: 'Expert Coaches', label_es: 'Entrenadoras Expertas' },
              { number: '1000+', label_en: 'Programs', label_es: 'Programas' },
              { number: '45', label_en: 'Countries', label_es: 'Países' }
            ].map((stat, index) => (
              <div key={index} className="space-y-2">
                <div className="text-4xl md:text-5xl font-bold text-emerald-400">{stat.number}</div>
                <div className="text-sm text-gray-400">
                  {language === 'en' ? stat.label_en : stat.label_es}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-emerald-400 rounded-full p-1">
          <div className="w-1 h-3 bg-emerald-400 rounded-full mx-auto animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;